import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  ColumnDirective,
  ColumnsDirective,
  CommandColumn,
  Edit,
  Filter,
  GridComponent,
  Page,
  Sort,
  Inject,
  Search,
  ColumnMenu,
  Toolbar,
} from "@syncfusion/ej2-react-grids";
import { db, auth } from "../../firebase";
import {
  getDoc,
  doc,
  collection,
  where,
  getDocs,
  query,
  updateDoc,
} from "firebase/firestore";
import { onAuthStateChanged, getAuth } from "firebase/auth";
import dataService from "../../service/data/DataService";
import { useNavigate } from "react-router-dom";
import { TextBoxComponent } from "@syncfusion/ej2-react-inputs";
import DatePickerTemplate from "../../components/DatePickerTemplate";
import Popup from "../../components/Popup";
import { CitizenForm } from "../../components/CitizenForm";
import Modal from "../../components/Modal";
import HeaderTitle from "../../components/HeaderTitle";
import viewEyeIcon from "../../assets/view-eye.svg";
import { ChevronDown } from "lucide-react";
import { useAuth } from "../../components/AuthContext";
import ThreeDotMenu from "../../components/ThreeDotMenu";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import AccessAccount from "../AccessAccount";

const filterSettings = {
  type: "CheckBox",
};

const sortSettings = {
  allowSorting: true,
};

const columnMenuItems = ["SortAscending", "SortDescending", "Filter"];

const pageSettings = { pageSize: 10 };

const editSettings = {
  allowEditing: true,
  allowAdding: true,
  showDeleteConfirmDialog: true,
  allowDeleting: true,
  allowEditOnDblClick: false,
  mode: "Dialog",
  dialog: { cssClass: "custom-dialog" },
};

const searchModalNavs = [
  {
    index: 0,
    label: "Citizen Search",
  },
  {
    index: 1,
    label: "Contacts",
  },
  {
    index: 2,
    label: "Disaster Recovery Partners Search",
  },
  {
    index: 3,
    label: "Local Non-Profit Coalition Search",
  },
];

// Helper functions
const formatPhoneNumber = (number) => {
  if (!number || typeof number !== "string" || number.length < 4) return "";

  // Only process if the number is at least 4 digits long
  const numberStr = number.toString().replace(/\D/g, ""); // Remove non-digits
  if (numberStr.length < 4) return number; // Return original if not enough digits

  return (
    <div>
      <span style={{ letterSpacing: "0.01em" }}>
        {"*".repeat(Math.max(0, numberStr.length - 4))}
      </span>
      {numberStr.slice(-4)}
    </div>
  );
};

const formatEmail = (email) => {
  if (!email) return "";
  const [localPart, domain] = email.split("@");
  if (!localPart || !domain) return email;
  return (
    <div>
      {localPart[0]}
      <span style={{ letterSpacing: "0.01em" }}>
        {"*".repeat(localPart.length - 2)}
      </span>
      {localPart[localPart.length - 1]}@{domain}
    </div>
  );
};

const fetchUserAndCenterData = async (userId) => {
  const docRef = doc(db, "users", userId);
  const userDoc = await getDoc(docRef);

  if (!userDoc.exists()) return { userData: null, centerData: null };

  const userData = userDoc.data();
  if (!userData.center_id) return { userData, centerData: null };

  const centerDoc = await getDoc(doc(db, "centers", userData.center_id));
  return {
    userData,
    centerData: centerDoc.exists() ? centerDoc.data() : null,
    centerId: userData.center_id,
  };
};

const searchCitizens = async (searchParams) => {
  let { firstName, lastName, birthDate, centerId, disasterId } = searchParams;

  // Convert search terms to lowercase
  firstName = firstName.toLowerCase();
  lastName = lastName.toLowerCase();

  if (!firstName || !lastName || !birthDate || !centerId) {
    console.error("Missing required search parameters");
    return [];
  }

  const queryRef = query(
    collection(db, "users"),
    where("center_id", "==", centerId),
    where("user_role", "==", "citizen"),
    // Remove the direct first_name and last_name comparisons since we'll filter after
    where("birth_date", "==", birthDate)
  );
  const querySnapshot = await getDocs(queryRef);

  const filteredCitizens = [];

  for (const userDoc of querySnapshot.docs) {
    const userData = userDoc.data();
    // Case-insensitive comparison of first and last names
    if (
      userData.first_name.toLowerCase() === firstName &&
      userData.last_name.toLowerCase() === lastName
    ) {
      const userId = userDoc.id;
      let shouldIncludeCitizen = !disasterId; // Include by default if no disasterId

      if (disasterId) {
        const surveyResponsesRef = collection(
          db,
          "users",
          userId,
          "survey_responses"
        );
        const surveyResponsesSnapshot = await getDocs(surveyResponsesRef);

        surveyResponsesSnapshot.forEach((surveyDoc) => {
          const disastersArray = surveyDoc.data().disasters || [];
          if (Array.isArray(disastersArray)) {
            if (
              disastersArray.some(
                (disaster) => disaster.disaster_id === disasterId
              )
            ) {
              shouldIncludeCitizen = true;
            }
          }
        });
      }

      if (shouldIncludeCitizen) {
        filteredCitizens.push({
          citizen_id: userDoc.id,
          darci_id: userData.darci_id || userData.username,
          name: `${userData.first_name} ${userData.last_name}`,
          address: `${userData.city}, ${userData.state}`,
          mobile_number: userData.mobile_number,
          email: userData.email,
          birth_date: userData.birth_date,
          has_access_to_rt: userData.has_access_to_rt || [],
        });
      }
    }
  }

  return filteredCitizens;
};

const CitizenSearch = ({ className = "" }) => {
  const gridRef = useRef(null);
  const [dataSource, setDataSource] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [toggleSideModal, setToggleSideModal] = useState(true);
  const [searchModalCurrNav, setSearchModalCurrNav] = useState(0);
  const [loading, setLoading] = useState(false);
  const [noResultsFound, setNoResultsFound] = useState(false);

  const [userId, setUserId] = useState(null);
  const [data, setData] = useState([]);
  const [gridData, setGridData] = useState([]);
  const selectedUserId = sessionStorage.getItem("userId");

  const [searchFirstName, setSearchFirstName] = useState("");
  const [searchLastName, setSearchLastName] = useState("");
  const [searchBirthDate, setSearchBirthDate] = useState("");

  const [validationErrors, setValidationErrors] = useState({});

  // Add ref for the timeout
  const errorTimeoutRef = useRef(null);

  const [centerId, setCenterId] = useState(null);

  // Add new state for popup
  const [showPopup, setShowPopup] = useState(false);

  // Add new state for CitizenForm modal
  const [showCitizenForm, setShowCitizenForm] = useState(false);

  const { login, setRoleBasedRoute } = useAuth();

  // Add new state at the top with other state declarations
  const [isBlueSky, setIsBlueSky] = useState(false);

  // Add this state to track if we've restored the search
  const [hasRestoredSearch, setHasRestoredSearch] = useState(false);

  // Add new state for access account modal
  const [showAccessAccount, setShowAccessAccount] = useState(false);
  const [accessAccountData, setAccessAccountData] = useState(null);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (selectedUserId) {
        try {
          setUserId(selectedUserId);
          const {
            userData,
            centerData,
            centerId: newCenterId,
          } = await fetchUserAndCenterData(selectedUserId);
          if (centerData) {
            setData(centerData);
            setCenterId(newCenterId);
          }
        } catch (error) {
          console.error("Error fetching user/center data:", error);
        }
      } else {
        setData(null);
        setCenterId(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Function to handle clicks outside the datepicker
    const handleClickOutside = (event) => {
      const datePickerElement = document.getElementById("birthDatePicker");
      const textBoxElement = document.getElementById("birthDateTextBox");

      // Check if click is inside the datepicker or any of its children (dropdowns)
      // We need to make sure clicks on the datepicker controls don't close it
      if (
        datePickerElement &&
        !datePickerElement.contains(event.target) &&
        !event.target.classList.contains("citizen-search-input") &&
        // Make sure we're not clicking on any dropdown elements
        !event.target.closest(".e-datepicker") &&
        !event.target.closest(".e-popup") &&
        !event.target.closest(".e-control")
      ) {
        datePickerElement.style.display = "none";
        // Show the textbox when clicking outside
        if (textBoxElement) {
          textBoxElement.style.display = "block";
        }
      }
    };

    // Add event listener
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const mapCitizenData = (citizens) => {
    return citizens.map((citizen) => ({
      id: citizen.id, // Keep this for grid purposes
      name: `${citizen.first_name} ${citizen.last_name}`,
      address: `${citizen.city},${" "} ${citizen.state}`,
      mobile_number: citizen.mobile_number,
      email: citizen.email,
      citizen_id: citizen.citizen_id, // Make sure this line is present
    }));
  };

  const onToggleGrid = () => {
    setToggleSideModal((prev) => !prev);
  };

  const onAddRecord = () => {
    gridRef.current.addRecord();
  };

  const onActionBegin = useCallback(
    (args) => {
      if (args.requestType === "save" && args.action === "add") {
        args.cancel = true;
        const newRecord = {
          ...args.data,
          id: (parseInt(dataSource.at(-1).id) + 1).toString(),
        };

        if (!!args.data.id) {
          const getIndexOfData = dataSource.findIndex(
            (d) => d.id === args.rowData.id
          );
          if (getIndexOfData < 0) return;
          setTimeout(() => {
            setDataSource((prev) => {
              prev.splice(getIndexOfData, 1, {
                ...newRecord,
                id: args.rowData.id,
              });
              return (prev = [...prev]);
            });
            args.cancel = false;
            gridRef.current.closeEdit();
            return args;
          }, 1000);
          return args;
        }

        // mimic the api call
        setTimeout(() => {
          args.cancel = false;
          setDataSource((prev) => {
            return (prev = [...prev, newRecord]);
          });
          gridRef.current.closeEdit();
          return args;
        }, 1000);
      }
      return args;
    },
    [dataSource]
  );

  const onActiveNav = useCallback((index) => {
    setSearchModalCurrNav(() => index);
  }, []);

  const onSearchDatabase = useCallback(
    (search) => {
      setSearchResults(() =>
        !search
          ? []
          : dataSource.filter((d) =>
              d.name.toLowerCase().includes(search.toLowerCase())
            )
      );
    },
    [dataSource]
  );

  useEffect(() => {
    // Only restore search if we haven't already
    if (!hasRestoredSearch) {
      const savedSearchState = sessionStorage.getItem("citizenSearchState");
      if (savedSearchState) {
        try {
          const parsedState = JSON.parse(savedSearchState);
          setSearchFirstName(parsedState.firstName || "");
          setSearchLastName(parsedState.lastName || "");
          setSearchBirthDate(parsedState.birthDate || "");
          setGridData(parsedState.searchResults || []);
          setIsBlueSky(parsedState.isBlueSky || false);
          setNoResultsFound(false);

          // Clear the saved state
          sessionStorage.removeItem("citizenSearchState");
        } catch (error) {
          console.error("Error restoring search state:", error);
        }
        // Mark that we've restored the search
        setHasRestoredSearch(true);
      }
    }
  }, [hasRestoredSearch]); // Only run when hasRestoredSearch changes

  const handleViewRecoveryPlan = async ({ rowData }) => {
    try {
      if (!rowData?.citizen_id) return;

      // Store current search state
      const searchState = {
        firstName: searchFirstName,
        lastName: searchLastName,
        birthDate: searchBirthDate,
        searchResults: gridData,
        isBlueSky: isBlueSky,
      };
      sessionStorage.setItem("citizenSearchState", JSON.stringify(searchState));

      // Store recovery tracker data
      sessionStorage.setItem("accessedUserId", rowData.citizen_id);
      sessionStorage.setItem("accessed_case_number", rowData.case_number);
      sessionStorage.setItem("accessed_disaster_id", selectedDisasterId);

      // Navigate to recovery tracker
      navigate("/viewrecovery/irs");
    } catch (error) {
      console.error("Error saving search state:", error);
    }
  };

  const handleCommandClick = (args) => {
    const citizenData = args.rowData;
    if (args.commandColumn.type === "View") {
      if (
        citizenData.has_access_to_rt &&
        citizenData.has_access_to_rt.includes(selectedUserId)
      ) {
        handleViewRecoveryPlan({ rowData: citizenData });
      } else {
        setShowPopup(true);
      }
    } else {
      if (
        citizenData.has_access_to_rt &&
        citizenData.has_access_to_rt.includes(selectedUserId)
      ) {
        // Open new tab and set session storage
        const newWindow = window.open("", "_blank");
        newWindow.sessionStorage.setItem("userId", citizenData.citizen_id);
        newWindow.location.href = window.location.origin + "/home";
      } else {
        // Show access account modal for users without access
        setAccessAccountData({
          email: citizenData.email,
          orgUserId: userId,
          orgType: "center",
          citizen_id: citizenData.citizen_id,
        });
        setShowAccessAccount(true);
      }
    }
  };

  const navigate = useNavigate();

  const renderSideModal = useMemo(() => {
    return (
      !toggleSideModal && (
        <div
          className="absolute right-0 top-0 z-10 flex h-full w-full flex-col items-end border border-solid"
          onClick={() => {
            setToggleSideModal(() => true);
          }}
        >
          <div
            className="mt-14 h-full w-[40%] space-y-[10px] bg-white p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-5 flex items-center space-x-[10px]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="21"
                viewBox="0 0 20 21"
                fill="none"
              >
                <path
                  opacity="0.4"
                  d="M14.6756 0.5H5.33333C1.92889 0.5 0 2.42889 0 5.83333V15.1667C0 18.5711 1.92889 20.5 5.33333 20.5H14.6756C18.08 20.5 20 18.5711 20 15.1667V5.83333C20 2.42889 18.08 0.5 14.6756 0.5Z"
                  fill="#348BFF"
                />
                <path
                  d="M5.36866 7.8689C4.91533 7.8689 4.54199 8.24223 4.54199 8.70445V15.5756C4.54199 16.0289 4.91533 16.4022 5.36866 16.4022C5.83088 16.4022 6.20421 16.0289 6.20421 15.5756V8.70445C6.20421 8.24223 5.83088 7.8689 5.36866 7.8689Z"
                  fill="#348BFF"
                />
                <path
                  d="M10.0352 4.58887C9.58183 4.58887 9.2085 4.9622 9.2085 5.42442V15.5755C9.2085 16.0289 9.58183 16.4022 10.0352 16.4022C10.4974 16.4022 10.8707 16.0289 10.8707 15.5755V5.42442C10.8707 4.9622 10.4974 4.58887 10.0352 4.58887Z"
                  fill="#348BFF"
                />
                <path
                  d="M14.6398 11.4956C14.1775 11.4956 13.8042 11.8689 13.8042 12.3312V15.5756C13.8042 16.0289 14.1775 16.4023 14.6309 16.4023C15.0931 16.4023 15.4664 16.0289 15.4664 15.5756V12.3312C15.4664 11.8689 15.0931 11.4956 14.6398 11.4956Z"
                  fill="#348BFF"
                />
              </svg>
              <p className="text-[12px] font-semibold text-[#348BFF]">
                Database
              </p>
            </div>
            <div className="flex justify-between">
              {searchModalNavs.map((nav, i) => {
                return (
                  <button
                    key={i}
                    className={`cursor-pointer bg-white px-[16px] py-[6px] text-[#348BFF] ${
                      searchModalCurrNav === nav.index &&
                      "border-b-[3px] border-solid border-[#348BFF] "
                    }`}
                    onClick={() => onActiveNav(nav.index)}
                  >
                    {nav.label}
                  </button>
                );
              })}
            </div>
            <div className="flex h-[35px] items-center overflow-hidden rounded-md border border-solid border-gray-500">
              <input
                placeholder="Search"
                className="h-full flex-1 px-3 focus:outline-none"
                onChange={(e) => onSearchDatabase(e.target.value)}
              />
              <div className="px-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="21"
                  height="22"
                  viewBox="0 0 21 22"
                  fill="none"
                >
                  <g id="Search">
                    <ellipse
                      id="Ellipse_746"
                      cx="9.59922"
                      cy="10.1532"
                      rx="8.59922"
                      ry="8.65324"
                      stroke="#DEE2E6"
                      stroke-width="2"
                    />
                    <path
                      id="Path_34202"
                      opacity="0.4"
                      d="M19.6745 21.4553C19.3405 21.4445 19.0228 21.307 18.7853 21.0705L16.7488 18.6902C16.3122 18.2909 16.2765 17.6123 16.6688 17.1689C16.8524 16.9831 17.102 16.8787 17.3624 16.8787C17.6228 16.8787 17.8725 16.9831 18.0561 17.1689L20.6172 19.2181C20.9861 19.5957 21.0999 20.1563 20.9078 20.6492C20.7157 21.1422 20.2535 21.4754 19.7279 21.5L19.6745 21.4553Z"
                      fill="#DEE2E6"
                    />
                  </g>
                </svg>
              </div>
            </div>
            <div className="flex flex-col space-y-2">
              {!!searchResults.length &&
                searchResults.map((ds) => {
                  return (
                    <div key={ds.id}>
                      <p className="font-semibold text-[#444444]">{ds.name}</p>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )
    );
  }, [
    toggleSideModal,
    onActiveNav,
    searchModalCurrNav,
    searchResults,
    onSearchDatabase,
  ]);

  const mobileNumberTemplate = (props) =>
    formatPhoneNumber(props.mobile_number);
  const emailTemplate = (props) => formatEmail(props.email || "");

  const validateSearchFields = () => {
    const errors = {};

    if (!searchFirstName.trim()) {
      errors.firstName = "First name is required";
    }

    if (!searchLastName.trim()) {
      errors.lastName = "Last name is required";
    }

    if (!searchBirthDate) {
      errors.birthDate = "Birth date is required";
    }

    setValidationErrors(errors);

    // Clear any existing timeout
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }

    // Set new timeout to clear errors after 3 seconds
    if (Object.keys(errors).length > 0) {
      errorTimeoutRef.current = setTimeout(() => {
        setValidationErrors({});
      }, 3000);
    }

    return Object.keys(errors).length === 0;
  };

  // Modify the handleSearch function to ignore disaster ID when BlueSky is checked
  const handleSearch = async () => {
    if (!validateSearchFields()) return;

    try {
      setLoading(true);
      setNoResultsFound(false);
      const searchParams = {
        firstName: searchFirstName.trim(),
        lastName: searchLastName.trim(),
        birthDate: searchBirthDate,
        centerId,
        ...(isBlueSky ? {} : { disasterId: selectedDisasterId }), // Only include disasterId if not BlueSky
      };

      const results = await searchCitizens(searchParams);

      if (results.length === 0) {
        setNoResultsFound(true);
      }

      setGridData(results);
    } catch (error) {
      console.error("Error searching users: ", error);
      setGridData([]);
      setNoResultsFound(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchFirstName("");
    setSearchLastName("");
    setSearchBirthDate("");
    setValidationErrors({});
    setGridData([]);
    setNoResultsFound(false);

    // Clear any existing timeout
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
  };

  const renderSearchFields = () => {
    return (
      <div className="mb-4 flex flex-wrap items-start justify-between">
        <div className="flex gap-4">
          <div className="flex flex-col">
            <TextBoxComponent
              placeholder="First Name"
              value={searchFirstName}
              change={(e) => {
                if (errorTimeoutRef.current) {
                  clearTimeout(errorTimeoutRef.current);
                }
                setSearchFirstName(e.value);
                setValidationErrors((prev) => ({ ...prev, firstName: "" }));
              }}
              cssClass={`citizen-search-input e-outline ${
                validationErrors.firstName ? "e-error" : ""
              }`}
              floatLabelType="Never"
              width="320px"
              keyUp={(e) => {
                if (e.keyCode === 13) handleSearch();
              }}
            />
            {validationErrors.firstName && (
              <div className="mt-1 text-xs text-red-500">
                {validationErrors.firstName}
              </div>
            )}
          </div>
          <div className="flex flex-col">
            <TextBoxComponent
              placeholder="Last Name"
              value={searchLastName}
              change={(e) => {
                if (errorTimeoutRef.current) {
                  clearTimeout(errorTimeoutRef.current);
                }
                setSearchLastName(e.value);
                setValidationErrors((prev) => ({ ...prev, lastName: "" }));
              }}
              cssClass={`citizen-search-input e-outline ${
                validationErrors.lastName ? "e-error" : ""
              }`}
              floatLabelType="Never"
              width="320px"
              keyUp={(e) => {
                if (e.keyCode === 13) handleSearch();
              }}
            />
            {validationErrors.lastName && (
              <div className="mt-1 text-xs text-red-500">
                {validationErrors.lastName}
              </div>
            )}
          </div>
          <div className="flex flex-col">
            <TextBoxComponent
              placeholder="Birth Date (MM/DD/YYYY)"
              value={searchBirthDate}
              change={(e) => {
                if (errorTimeoutRef.current) {
                  clearTimeout(errorTimeoutRef.current);
                }
                setSearchBirthDate(e.value);
                setValidationErrors((prev) => ({ ...prev, birthDate: "" }));
              }}
              cssClass={`citizen-search-input e-outline ${
                validationErrors.birthDate ? "e-error" : ""
              }`}
              floatLabelType="Never"
              width="320px"
              focus={() => {
                // Show DatePicker when the field is clicked
                const datePickerElement =
                  document.getElementById("birthDatePicker");
                const textBoxElement =
                  document.getElementById("birthDateTextBox");
                if (datePickerElement) {
                  datePickerElement.style.display = "block";
                  datePickerElement.style.width = "280px";
                  datePickerElement.style.borderRadius = "4px";
                  datePickerElement.style.backgroundColor = "white";

                  // Focus the datepicker to ensure it's interactive
                  setTimeout(() => {
                    const datepickerInput =
                      datePickerElement.querySelector("input");
                    if (datepickerInput) {
                      datepickerInput.focus();
                    }
                  }, 100);

                  if (textBoxElement) {
                    textBoxElement.style.display = "none";
                  }
                }
              }}
              keyUp={(e) => {
                if (e.keyCode === 13) handleSearch();
              }}
              id="birthDateTextBox"
            />
            <div
              id="birthDatePicker"
              style={{
                display: "none",
                position: "absolute",
                zIndex: 1000,
                marginTop: "2px",
                marginLeft: "30px",
              }}
            >
              <DatePickerTemplate
                value={searchBirthDate}
                onChange={(val) => {
                  // Format date to ensure leading zeros
                  const formattedDate = val
                    ? new Date(val).toLocaleDateString("en-US", {
                        month: "2-digit",
                        day: "2-digit",
                        year: "numeric",
                      })
                    : "";
                  setSearchBirthDate(formattedDate);
                  setValidationErrors((prev) => ({ ...prev, birthDate: "" }));

                  // Hide datepicker after selection
                  const datePickerElement =
                    document.getElementById("birthDatePicker");
                  const textBoxElement =
                    document.getElementById("birthDateTextBox");
                  if (datePickerElement) {
                    datePickerElement.style.display = "none";
                  }
                  if (textBoxElement) {
                    textBoxElement.style.display = "block";
                  }
                }}
                key={searchBirthDate}
                style={{ width: "250px" }}
                cssClass="e-custom-datepicker"
                enablePersistence={true}
                showClearButton={false}
                strictMode={false}
                openOnFocus={true}
              />
            </div>
            {validationErrors.birthDate && (
              <div className="mt-1 text-xs text-red-500">
                {validationErrors.birthDate}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-end">
          <div className="flex items-start gap-2">
            <button
              type="button"
              className="text-lg h-[56px] w-[200px] cursor-pointer rounded-[4px] bg-[#0a2558] font-semibold text-[#F7CA41] hover:bg-[#0a2558]/90"
              onClick={handleSearch}
            >
              Search
            </button>
            <button
              type="button"
              className="text-lg h-[56px] w-[200px] cursor-pointer rounded-[4px] border border-[#0a2558] bg-[#FFF]   font-semibold text-[#0a2558]"
              onClick={handleClearSearch}
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add handler for form submission success
  const handleFormSuccess = () => {
    setShowCitizenForm(false);
    // Optionally refresh the search results
    handleSearch();
  };

  const [userData, setUserData] = useState("");
  const [activeData, setActiveData] = useState([]);
  const [userRole, setUserRole] = useState("");
  const dropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [disasterNames, setDisasterNames] = useState([]);
  const [selectedDisaster, setSelectedDisaster] = useState("");
  const [selectedDisasterId, setSelectedDisasterId] = useState("");

  const auth = getAuth();
  const user = auth.currentUser;

  useEffect(() => {
    const fetchUserData = async () => {
      if (user && selectedUserId) {
        const userDoc = await getDoc(doc(db, "users", selectedUserId));

        if (userDoc.exists()) {
          const role = userDoc.data().user_role;
          const userData = userDoc.data();
          setUserRole(role);

          let updatedUserData = userData;

          if (role === "center") {
            const centerDoc = await getDoc(
              doc(db, "centers", userData.center_id)
            );
            if (centerDoc.exists()) {
              updatedUserData = centerDoc.data();
            }
          } else if (role === "partner") {
            const partnerDoc = await getDoc(
              doc(db, "partners", userData?.partner_id)
            );
            if (partnerDoc.exists()) {
              updatedUserData = partnerDoc.data();
            }
          }

          setUserData(updatedUserData);

          try {
            let disasters = [];
            let activeData = [];

            if (role === "partner") {
              if (!userData?.center_id) {
                setLoading(false);
                return;
              }

              const centerIds = Array.isArray(userData.center_id)
                ? userData.center_id
                : [userData.center_id];

              if (centerIds.length === 0) {
                setLoading(false);
                return;
              }

              const counties = updatedUserData?.area_serviced_county || [];

              if (counties.length === 0) {
                setLoading(false);
                return;
              }

              const chunks = [];
              for (let i = 0; i < counties.length; i += 10) {
                chunks.push(counties.slice(i, i + 10));
              }

              const seenIds = new Set();

              for (const chunk of chunks) {
                const q = query(
                  collection(db, "declared-disaster"),
                  where("disaster_status", "==", "active"),
                  where("is_deleted", "==", false),
                  where("center_id", "in", centerIds),
                  where("affected_counties", "array-contains-any", chunk)
                );

                const snapshot = await getDocs(q);

                snapshot.forEach((doc) => {
                  if (!seenIds.has(doc.id)) {
                    seenIds.add(doc.id);
                    disasters.push({
                      id: doc.id,
                      name: doc.data().disaster_name,
                    });

                    activeData.push({
                      disaster_id: doc.id,
                      disaster_name: doc.data().disaster_name,
                      disaster_status: doc.data().disaster_status,
                      start_date: doc.data().start_date
                        ? doc
                            .data()
                            .start_date.toDate()
                            .toLocaleDateString("en-US", {
                              month: "2-digit",
                              day: "2-digit",
                              year: "numeric",
                            })
                        : null,
                      county: doc.data().affected_counties,
                    });
                  }
                });
              }
            } else if (role === "center") {
              if (!userData?.center_id) {
                setLoading(false);
                return;
              }

              const q = query(
                collection(db, "declared-disaster"),
                where("disaster_status", "==", "active"),
                where("is_deleted", "==", false),
                where("center_id", "==", userData.center_id),
                where(
                  "affected_counties",
                  "array-contains",
                  updatedUserData?.county
                )
              );

              const querySnapshot = await getDocs(q);

              disasters = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                name: doc.data().disaster_name,
              }));

              activeData = querySnapshot.docs.map((doc) => ({
                disaster_id: doc.id,
                disaster_name: doc.data().disaster_name,
                disaster_status: doc.data().disaster_status,
                start_date: doc.data().start_date
                  ? doc.data().start_date.toDate().toLocaleDateString("en-US", {
                      month: "2-digit",
                      day: "2-digit",
                      year: "numeric",
                    })
                  : null,
                county: doc.data().affected_counties,
              }));
            }

            setDisasterNames(disasters);
            setActiveData(activeData);

            if (disasters.length > 0) {
              setSelectedDisaster(disasters[0].name);
              setSelectedDisasterId(disasters[0].id);
            }

            setLoading(false);
          } catch (error) {
            console.error("Error fetching disaster names:", error);
            setLoading(false);
          }
        }
      }
    };

    fetchUserData();
  }, [user, selectedUserId]);

  const [activeMenuCitizen, setActiveMenuCitizen] = useState(null);

  return (
    <>
      <div
        className={`m-0 box-border flex w-[1648px] max-w-full flex-col items-start justify-start gap-1 px-2.5 py-[15px] leading-[normal] tracking-[normal] ${className}`}
      >
        <HeaderTitle id="communitycontacts" title="Citizen Search" />
        <div className="mb-2 flex w-full items-center justify-between">
          <div>
            <div className="font-Inter text-md relative mb-2 inline-block max-w-full font-medium leading-[21px] text-darkslategray-200">
              <p>
                Centers only have access to citizens that have authorized their
                information to be shared with a center.
              </p>
            </div>
          </div>

          {/* Add Citizen Button */}
          <button
            onClick={() => setShowCitizenForm(true)}
            className="text-lg h-[56px] w-[200px] cursor-pointer rounded-[4px] bg-[#0a2558] font-semibold text-[#F7CA41] hover:bg-[#0a2558]/90"
          >
            <div className="flex items-center justify-center gap-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <line x1="19" y1="8" x2="19" y2="14" />
                <line x1="16" y1="11" x2="22" y2="11" />
              </svg>
              Add Citizen
            </div>
          </button>
        </div>
        {/* Report Generator Start */}
        {disasterNames.length > 0 && (
          <div className="my-2 flex items-center gap-4">
            <div className="flex-1 rounded border border-solid border-[#8F8F8F]">
              <div className="" ref={dropdownRef}>
                <div className="relative w-[300px] cursor-pointer rounded px-2">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    disabled={isBlueSky}
                    className={`relative flex w-full cursor-pointer items-center justify-between gap-1.5 bg-transparent px-0 py-2 text-[17px] ${
                      isBlueSky ? "text-gray-400" : "text-smokey-grey"
                    }`}
                  >
                    <span className="truncate">
                      {selectedDisaster
                        ? selectedDisaster
                        : "Select a Disaster"}
                    </span>
                    <ChevronDown className="h-4 w-4" strokeWidth={2} />
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute z-10 -ml-2 mt-1 w-[300px] rounded border border-gray-300 bg-white shadow-lg">
                      <div className="max-h-60 overflow-y-auto">
                        {disasterNames.map((disaster) => (
                          <div
                            key={disaster.id}
                            className="hover:bg-gray-100 cursor-pointer px-4 py-2"
                            onClick={() => {
                              setSelectedDisasterId(disaster.id);
                              setSelectedDisaster(disaster.name);
                              setIsDropdownOpen(false);
                            }}
                          >
                            {disaster.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Add BlueSky checkbox */}
            <div className="flex items-center gap-2">
              <div className="relative flex items-center">
                <input
                  type="checkbox"
                  checked={isBlueSky}
                  onChange={(e) => setIsBlueSky(e.target.checked)}
                  className="peer h-6 w-6 cursor-pointer appearance-none rounded-[2px] border-2 border-[#003460] bg-white shadow-[inset_0_0_0_1px_#003460] checked:border-[#003460] checked:bg-[#003460] checked:shadow-none"
                />
                <svg
                  className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 peer-checked:opacity-100"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.3334 4L6.00008 11.3333L2.66675 8"
                    stroke="#F7CA41"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span className="text-base font-medium text-[#242424]">
                BlueSky
              </span>
            </div>
          </div>
        )}
        <div id="dialog-container"></div>
        <section className="flex max-w-full flex-col items-start justify-start self-stretch bg-white">
          <div>
            {renderSearchFields()}

            {loading ? (
              <div className="flex flex-col items-center justify-center p-8">
                <p className="text-gray-600 mb-4 text-center">
                  Searching for Results....
                </p>
                <div className="h-2 w-full max-w-3xl rounded-full bg-gray-200">
                  <div className="h-full w-1/2 animate-pulse rounded-full bg-blue-600"></div>
                </div>
              </div>
            ) : (
              <GridComponent
                ref={gridRef}
                dataSource={gridData}
                allowSorting={true}
                sortSettings={sortSettings}
                filterSettings={filterSettings}
                allowFiltering={true}
                actionBegin={onActionBegin}
                allowPaging={true}
                pageSettings={pageSettings}
                showColumnMenu={true}
                columnMenuItems={columnMenuItems}
                commandClick={handleCommandClick}
                allowSelection={true}
                selectionSettings={{ type: "Single" }}
                cssClass="new-grid-color allow-overflow-grid"
              >
                <ColumnsDirective>
                  <ColumnDirective
                    field="darci_id"
                    headerText="Darci ID"
                    width="200"
                  />
                  <ColumnDirective field="name" headerText="Name" width="200" />
                  <ColumnDirective
                    field="mobile_number"
                    headerText="Mobile Number"
                    width="180"
                    template={mobileNumberTemplate}
                  />
                  <ColumnDirective
                    field="email"
                    headerText="Email Address"
                    width="250"
                    template={emailTemplate}
                  />
                  <ColumnDirective
                    field="address"
                    headerText="Location"
                    width="150"
                  />

                  <ColumnDirective
                    headerText="Actions"
                    textAlign="Center"
                    width="100"
                    template={(props) => {
                      return (
                        <div className="flex w-full justify-center overflow-visible px-4">
                          <ThreeDotMenu
                            menuId={props?.citizen_id}
                            activeMenuId={activeMenuCitizen}
                            setActiveMenuId={setActiveMenuCitizen}
                            clickOutside={false}
                            customItems={[
                              {
                                label: "View Recovery Tracker",
                                onClick: (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setActiveMenuCitizen(null);
                                  if (
                                    props.has_access_to_rt &&
                                    Array.isArray(props.has_access_to_rt) &&
                                    props.has_access_to_rt.includes(
                                      selectedUserId
                                    )
                                  ) {
                                    handleViewRecoveryPlan({ rowData: props });
                                  } else {
                                    setShowPopup(true);
                                  }
                                },
                              },
                              {
                                label: "Access Account",
                                onClick: (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setActiveMenuCitizen(null);

                                  if (
                                    props.has_access_to_rt &&
                                    Array.isArray(props.has_access_to_rt) &&
                                    props.has_access_to_rt.includes(
                                      selectedUserId
                                    )
                                  ) {
                                    // Open new tab and set session storage
                                    const newWindow = window.open("", "_blank");
                                    newWindow.sessionStorage.setItem(
                                      "userId",
                                      props.citizen_id
                                    );
                                    newWindow.location.href =
                                      window.location.origin + "/home";
                                  } else {
                                    // Show access account modal for users without access
                                    setAccessAccountData({
                                      email: props.email,
                                      orgUserId: userId,
                                      orgType: "center",
                                      citizen_id: props.citizen_id,
                                    });
                                    setShowAccessAccount(true);
                                  }
                                },
                              },
                            ]}
                          />
                        </div>
                      );
                    }}
                  />
                </ColumnsDirective>
                <Inject
                  services={[
                    Page,
                    Edit,
                    Toolbar,
                    Sort,
                    Filter,
                    ColumnMenu,
                    CommandColumn,
                  ]}
                />
              </GridComponent>
            )}
          </div>
        </section>
      </div>
      {renderSideModal}
      {showPopup && (
        <Popup
          title="Access Denied"
          message="This citizen has not authorized access to their recovery tracker."
          onClose={() => setShowPopup(false)}
        />
      )}
      {noResultsFound && (
        <Popup
          title="No Results Found"
          message="No citizens found matching your search criteria. Please try again with different information."
          onClose={() => setNoResultsFound(false)}
        />
      )}
      {/* Citizen Form Modal */}
      {showCitizenForm && (
        <Modal
          isOpen={showCitizenForm}
          onClose={() => setShowCitizenForm(false)}
          title="Add New Citizen"
          width="80%"
          height="85%"
          noFooter={true}
        >
          <CitizenForm
            onSubmitSuccess={handleFormSuccess}
            centerState={data?.state}
            centerCounty={data?.county}
            centerId={centerId}
            centerEmail={data?.email}
            fromCitizenSearchRegular={true}
          />
        </Modal>
      )}
      {/* Access Account Modal */}
      <AccessAccount
        isOpen={showAccessAccount}
        onClose={() => {
          setShowAccessAccount(false);
          setAccessAccountData(null);
        }}
        citizenData={accessAccountData}
        orgType={"center"}
      />
    </>
  );
};

export default CitizenSearch;
